/**
 * useReservationActions — Ted #32c / epic E3.1–E3.2 (#1158): Cancel, Edit and
 * No-show on a reservation, for ANY surface that lists one.
 *
 * Until now the cancel confirm and the Edit dialog were wired into the
 * Bookings page alone (`Bookings.tsx`), so the Front Desk's and the
 * dashboard's Expected lists — the rows Ted works from — could check a dog in
 * and undo it and nothing else. This hook is that wiring, lifted out: it owns
 * the three dialogs, the one call behind each, and the notice that says what
 * happened, so every surface asks the same question in the same words.
 *
 *   const reservation = useReservationActions({ apiUrl, onChanged: refresh });
 *   …
 *   <button onClick={() => reservation.cancel(target)} />
 *   {reservation.banner}
 *   {reservation.dialogs}
 *
 * WHAT A SURFACE KNOWS differs, and the hook absorbs it:
 *   - The Bookings page holds the full `AdminBooking` — Edit opens at once.
 *   - Front Desk / dashboard rows are `/today` summaries with no service-type
 *     id and no stay dates, which the Edit dialog needs. The hook reads the
 *     one row it needs through the SAME `/schedule` call the pending queue
 *     uses (filtered to that owner and that day), so what the dialog edits is
 *     the server's own record, never a reconstruction.
 *
 * Cancel and no-show are the two ways an expected reservation closes without
 * a visit, and NEITHER charges anyone: there is no invoice before check-out.
 * Both say so in the confirm, and in the notice afterwards.
 *
 * `performCancel` lets a surface keep its own cancel path (the Bookings page's
 * optimistic flip + rollback) while still using the shared confirm.
 *
 * RECURRING SERIES (Ted #30 / #986, #1109 AC3, lane LR): Edit on an occurrence
 * still attached to a series asks Ted's question first, "Change this
 * reservation only" or "Change this and future reservations", and the Edit
 * dialog then commits through the series route with that scope. It lives here,
 * not on one page, so every surface that offers Edit asks it.
 */
import { useCallback, useState } from 'react';
import {
  cancelBooking,
  editSeriesOccurrence,
  fetchAdminSchedule,
  fetchServiceTypes,
  readSession,
  type AdminBooking,
  type SeriesEditScope,
  type ServiceTypeSummary,
} from '../../lib/api.js';
import { CancelReservationDialog } from './CancelReservationDialog.js';
import { EditReservationDialog } from './EditReservationDialog.js';
import { NoShowDialog } from './NoShowDialog.js';
import { RecurringEditScopeDialog } from './RecurringEditScopeDialog.js';
import { markBookingNoShow } from './no-show.js';

/** What a surface hands over when the desk taps an action on a row. */
export interface ReservationTarget {
  readonly bookingId: string;
  /** The dialog header — dogs and owner, never an id: "Biscuit — Wren Halloway". */
  readonly label: string;
  /** The dog(s) alone, for the notice: "Biscuit". */
  readonly dogLabel: string;
  /** The owner — narrows the Edit lookup to one household. Optional (older servers). */
  readonly customerId?: string | null;
  /** The facility day the row is listed under (YYYY-MM-DD), for the Edit lookup. */
  readonly day: string;
  /** One half of a boarding+grooming combo — cancelling takes its partner too. */
  readonly hasComboPartner?: boolean;
  /** The full row, when the surface already holds it — Edit then needs no lookup. */
  readonly booking?: AdminBooking;
}

export interface ReservationNotice {
  readonly tone: 'success' | 'warning';
  readonly text: string;
}

type Open =
  | { readonly kind: 'cancel'; readonly target: ReservationTarget }
  | { readonly kind: 'no-show'; readonly target: ReservationTarget }
  // Ted #30 — an attached series occurrence: the scope question, before Edit.
  | { readonly kind: 'series-scope'; readonly target: ReservationTarget; readonly booking: AdminBooking }
  | {
      readonly kind: 'edit';
      readonly target: ReservationTarget;
      readonly booking: AdminBooking;
      /** Set when the row is a series occurrence and the desk chose a scope. */
      readonly scope?: SeriesEditScope;
    };

/**
 * Ted #30 — an occurrence still attached to its series asks the scope question.
 * A detached one ("changed on its own") is a plain reservation again.
 * `series_id` is a key only the new server emits; absent ⇒ a plain edit.
 */
function asksSeriesScope(booking: AdminBooking): boolean {
  return Boolean(booking.series_id) && !booking.series_detached_at;
}

export interface UseReservationActionsArgs {
  readonly apiUrl: string;
  /** Called after any change lands, so the surface refreshes its list. */
  readonly onChanged: () => void;
  /**
   * The surface's own cancel, run after the shared confirm. Omitted ⇒ the hook
   * cancels through `POST …/cancel`, tells the desk, and calls `onChanged`.
   */
  readonly performCancel?: (target: ReservationTarget) => void | Promise<void>;
  /** The catalog, when the surface already holds it. Omitted ⇒ read on first Edit. */
  readonly serviceTypes?: readonly ServiceTypeSummary[];
  /** The banner's data-testid — a surface with an existing contract keeps its own. */
  readonly bannerTestId?: string;
}

export interface UseReservationActionsResult {
  readonly cancel: (target: ReservationTarget) => void;
  readonly edit: (target: ReservationTarget) => void;
  readonly noShow: (target: ReservationTarget) => void;
  /** Rows with an action in flight — the surface disables their buttons. */
  readonly busyIds: ReadonlySet<string>;
  /** The page's own notice for what just happened; null when there is none. */
  readonly notice: ReservationNotice | null;
  readonly dismissNotice: () => void;
  /** The notice as a ready-made banner (status / alert role by tone). */
  readonly banner: JSX.Element | null;
  /** The open dialog, or null. Render once per surface. */
  readonly dialogs: JSX.Element | null;
}

const NOT_FOUND_NOTICE =
  'Couldn’t open that reservation — it may have changed. Refresh the list and try again.';

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message.length > 0 ? err.message : fallback;
}

export function useReservationActions({
  apiUrl,
  onChanged,
  performCancel,
  serviceTypes: providedServiceTypes,
  bannerTestId = 'reservation-action-notice',
}: UseReservationActionsArgs): UseReservationActionsResult {
  const [open, setOpen] = useState<Open | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [notice, setNotice] = useState<ReservationNotice | null>(null);
  const [noShowError, setNoShowError] = useState<string | null>(null);
  const [loadedServiceTypes, setLoadedServiceTypes] = useState<readonly ServiceTypeSummary[]>([]);

  const markBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const close = useCallback(() => {
    setOpen(null);
    setNoShowError(null);
  }, []);

  const cancel = useCallback((target: ReservationTarget) => {
    setNotice(null);
    setOpen({ kind: 'cancel', target });
  }, []);

  const noShow = useCallback((target: ReservationTarget) => {
    setNotice(null);
    setNoShowError(null);
    setOpen({ kind: 'no-show', target });
  }, []);

  const edit = useCallback(
    (target: ReservationTarget) => {
      setNotice(null);
      const { token } = readSession();
      if (!token) return;
      void (async () => {
        markBusy(target.bookingId, true);
        try {
          let booking = target.booking ?? null;
          if (!booking) {
            const rows = await fetchAdminSchedule(apiUrl, token, {
              startDate: target.day,
              endDate: target.day,
              ...(target.customerId ? { customerId: target.customerId } : {}),
            });
            booking = rows.find((r) => r.id === target.bookingId) ?? null;
          }
          if (!booking) {
            setNotice({ tone: 'warning', text: NOT_FOUND_NOTICE });
            return;
          }
          if (providedServiceTypes === undefined && loadedServiceTypes.length === 0) {
            // Non-fatal: the dialog still opens, it just cannot offer a service change.
            setLoadedServiceTypes(await fetchServiceTypes(apiUrl, token).catch(() => []));
          }
          setOpen(
            asksSeriesScope(booking)
              ? { kind: 'series-scope', target, booking }
              : { kind: 'edit', target, booking },
          );
        } catch (err) {
          setNotice({ tone: 'warning', text: messageOf(err, NOT_FOUND_NOTICE) });
        } finally {
          markBusy(target.bookingId, false);
        }
      })();
    },
    [apiUrl, loadedServiceTypes.length, markBusy, providedServiceTypes],
  );

  const confirmCancel = useCallback(
    async (target: ReservationTarget) => {
      setOpen(null);
      if (performCancel) {
        await performCancel(target);
        return;
      }
      const { token } = readSession();
      if (!token) return;
      markBusy(target.bookingId, true);
      try {
        await cancelBooking(apiUrl, token, target.bookingId);
        setNotice({
          tone: 'success',
          text: `${target.dogLabel}’s reservation is cancelled. Nobody is charged.`,
        });
        onChanged();
      } catch (err) {
        setNotice({
          tone: 'warning',
          text: `Not cancelled — ${messageOf(err, 'the reservation could not be cancelled')}`,
        });
      } finally {
        markBusy(target.bookingId, false);
      }
    },
    [apiUrl, markBusy, onChanged, performCancel],
  );

  const confirmNoShow = useCallback(
    async (target: ReservationTarget, reason: string) => {
      markBusy(target.bookingId, true);
      setNoShowError(null);
      try {
        await markBookingNoShow(apiUrl, target.bookingId, reason);
        setOpen(null);
        setNotice({
          tone: 'success',
          text: `${target.dogLabel} is marked a no-show and is off the Expected list. Nobody is charged.`,
        });
        onChanged();
      } catch (err) {
        // The dialog stays open on the server's own sentence ("This dog is
        // already checked in…") so the desk reads why, rather than a toast
        // that vanishes over a dialog that closed.
        setNoShowError(messageOf(err, 'Could not mark that reservation a no-show.'));
      } finally {
        markBusy(target.bookingId, false);
      }
    },
    [apiUrl, markBusy, onChanged],
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  const banner = notice ? (
    <div
      className={`ferro-banner ferro-banner--${notice.tone}`}
      role={notice.tone === 'warning' ? 'alert' : 'status'}
      data-testid={bannerTestId}
      data-tone={notice.tone}
    >
      <p>{notice.text}</p>
      <button type="button" className="ferro-btn ferro-btn--ghost" onClick={dismissNotice}>
        Dismiss
      </button>
    </div>
  ) : null;

  let dialogs: JSX.Element | null = null;
  if (open?.kind === 'cancel') {
    dialogs = (
      <CancelReservationDialog
        label={open.target.label}
        hasComboPartner={open.target.hasComboPartner === true}
        busy={busyIds.has(open.target.bookingId)}
        onKeep={close}
        onConfirm={() => void confirmCancel(open.target)}
      />
    );
  } else if (open?.kind === 'no-show') {
    dialogs = (
      <NoShowDialog
        label={open.target.label}
        busy={busyIds.has(open.target.bookingId)}
        error={noShowError}
        onKeep={close}
        onConfirm={(reason) => void confirmNoShow(open.target, reason)}
      />
    );
  } else if (open?.kind === 'series-scope') {
    const { target, booking } = open;
    dialogs = (
      <RecurringEditScopeDialog
        label={target.label}
        onChoose={(scope) => setOpen({ kind: 'edit', target, booking, scope })}
        onClose={close}
      />
    );
  } else if (open?.kind === 'edit') {
    const token = readSession().token ?? '';
    const { booking, scope } = open;
    const seriesId = booking.series_id;
    dialogs = (
      <EditReservationDialog
        apiUrl={apiUrl}
        token={token}
        booking={booking}
        label={open.target.label}
        // Sawyer 07 quickbook-bed-addons (B1) — the dog alone, for the bed sentences.
        dogLabel={open.target.dogLabel}
        serviceTypes={providedServiceTypes ?? loadedServiceTypes}
        onClose={close}
        onSaved={(summary) => {
          setNotice({ tone: 'success', text: summary });
          onChanged();
        }}
        // Ted #30 — a series occurrence commits through the series route with
        // the scope the desk chose; every other row through the plain edit.
        {...(scope && seriesId
          ? { commit: (patch) => editSeriesOccurrence(apiUrl, token, seriesId, booking.id, scope, patch) }
          : {})}
      />
    );
  }

  return { cancel, edit, noShow, busyIds, notice, dismissNotice, banner, dialogs };
}
